// Every color has hex chroma (max-min channel) >= 100, which keeps routes
// visually distinct after the shared map applies its muted default opacity.
export const ROUTE_COLORS = [
  '#1565C0',
  '#C62828',
  '#7B1FA2',
  '#00838F',
  '#E65100',
  '#AD1457',
  '#0D47A1',
  '#BF360C',
  '#6A1B9A',
  '#00695C',
  '#C2185B',
  '#1A237E',
  '#880E4F',
  '#4527A0',
  '#0277BD',
  '#B71C1C',
];

export const formatRouteLabel = (key: string): string => {
  const routeNumber = key.match(/(?:^|_)(\d+)$/)?.[1];
  return routeNumber
    ? `Route ${routeNumber}`
    : key
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (character) => character.toUpperCase());
};

// Short, GM-facing name for a fleet vehicle type. The backend stores each type's roster name; the pilot's
// van roster row is a model name ("RAM Promaster 3500"), so it is shortened to "Van" for the legend. Any
// other type (e.g. "Box Truck") is already display-ready and shown verbatim.
export const formatVehicleType = (vehicleType: string): string =>
  vehicleType === 'RAM Promaster 3500' ? 'Van' : vehicleType;

// "night_time" -> "Night Time": the short, GM-facing shift name used to label a route with the shift it
// ran on (sc-66833).
export const formatShiftLabel = (shiftType: string): string =>
  shiftType
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

// A warehouse-local "HH:MM" (24h) clock string -> "h:mm AM/PM". Shift start/end come off WarehouseShift as
// wall-clock strings; a malformed value falls back to the raw string rather than rendering "NaN".
const formatClock = (hhmm: string): string => {
  const [hours, minutes] = hhmm.split(':').map(Number);
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return hhmm;
  const period = hours >= 12 ? 'PM' : 'AM';
  const hour12 = hours % 12 === 0 ? 12 : hours % 12;
  return `${hour12}:${String(minutes).padStart(2, '0')} ${period}`;
};

// "night_time", "20:00", "04:00" -> "Night Time (8:00 PM–4:00 AM)": the shift name plus its configured
// window, for labeling a route with the shift it ran on (sc-66833).
export const formatShiftWindowLabel = (
  shiftType: string,
  startTime: string,
  endTime: string,
): string =>
  `${formatShiftLabel(shiftType)} (${formatClock(startTime)}–${formatClock(
    endTime,
  )})`;

// A route's legend label, suffixed with the vehicle type that served it when known (sc-65178) — e.g.
// "Route 1 · Box Truck". The number is the route's 1-based position in the run's route list, not a value
// parsed from the route key: the backend numbers each cohort's routes from 1, so keying off the key's
// trailing digit would restart the count per cohort (sc-66596). Callers pass the display index so the
// numbering runs sequentially top to bottom, matching the Deliveries page. Falls back to the bare label
// when the run carries no type for the route (pre-composition runs, or a vehicle the join couldn't resolve).
export const routeLabelWithVehicleType = (
  routeNumber: number,
  vehicleType?: string,
  shiftLabel?: string,
): string => {
  const label = `Route ${routeNumber}`;
  const withType = vehicleType
    ? `${label} · ${formatVehicleType(vehicleType)}`
    : label;
  // The shift the route ran on (sc-66833), appended after the type — e.g. "Route 1 · Van · Night Time".
  // Absent for runs not built from a fleet shift assignment, leaving the label exactly as before.
  return shiftLabel ? `${withType} · ${shiftLabel}` : withType;
};

export const firstNumericToken = (value: string): number => {
  const match = value.match(/\d+/);
  return match ? Number(match[0]) : Number.POSITIVE_INFINITY;
};

type DeliveryAddress = {
  addressLine1: string;
  latitude?: number | null;
  longitude?: number | null;
};

type DeliveryStopLike = {
  __typename?: string;
  id?: string;
  location?: { businessName: string; address?: DeliveryAddress | null } | null;
};

type DeliveryStopAddress = {
  id: string;
  businessName: string;
  address: DeliveryAddress;
};

export const getDeliveryStopAddress = (
  stop: DeliveryStopLike,
): DeliveryStopAddress | null => {
  if (stop.__typename !== 'Delivery' || typeof stop.id !== 'string') {
    return null;
  }
  const location = stop.location;
  const address = location?.address;
  return location && address
    ? { id: stop.id, businessName: location.businessName, address }
    : null;
};
